import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Building2, ArrowRight, AlertTriangle, Check } from "lucide-react";
import clienteApi from "@/lib/clienteApi";
import { toast } from "sonner";

/**
 * PortariaGuardModal
 *
 * Modal "guard" exibido no `ClienteLayout` enquanto o operador não tem uma
 * portaria de trabalho selecionada.
 *
 * Regras:
 * - NÃO aparece quando há portaria selecionada (sessionStorage `brave_cliente_portaria`).
 * - NÃO aparece em `/cliente/portarias` (página onde o operador cadastra/edita).
 * - Quando existem portarias: lista as opções para o operador selecionar dentro do modal.
 * - Quando NÃO existem portarias: mensagem explicativa + botão "Incluir Portarias".
 *
 * Expõe estado global via `window.__bravePortariaBloqueada` e dispatcha
 * `brave:portaria-bloqueio-changed` para o layout reagir e desabilitar UI.
 */
export default function PortariaGuardModal() {
  const navigate = useNavigate();
  const location = useLocation();
  const clienteData = JSON.parse(sessionStorage.getItem("brave_cliente_data") || "{}");
  const clienteId = clienteData?.id || "";
  const lastKey = `brave_cliente_portaria_last_${clienteId}`;

  const [portarias, setPortarias] = useState(null); // null=loading, array=loaded
  const [temSelecionada, setTemSelecionada] = useState(() => {
    try { return !!JSON.parse(sessionStorage.getItem("brave_cliente_portaria") || "null"); }
    catch { return false; }
  });

  // Listener de mudança da portaria
  useEffect(() => {
    const onChange = () => {
      try { setTemSelecionada(!!JSON.parse(sessionStorage.getItem("brave_cliente_portaria") || "null")); }
      catch { setTemSelecionada(false); }
    };
    window.addEventListener("brave:portaria-changed", onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener("brave:portaria-changed", onChange);
      window.removeEventListener("storage", onChange);
    };
  }, []);

  // Fetch das portarias (refetch ao trocar de rota para refletir cadastros recém-feitos).
  // Após o fetch, tenta restaurar a portaria decorada do localStorage (mesma lógica
  // do PortariaSwitcher) — evita flash do modal em PCs que já têm portaria salva.
  useEffect(() => {
    let cancel = false;
    clienteApi
      .get("/cliente/portarias")
      .then((r) => {
        if (cancel) return;
        const arr = Array.isArray(r.data) ? r.data : [];
        setPortarias(arr);
        // Restauração silenciosa: se não houver portaria selecionada mas houver registro
        // decorado por cliente, restaura sem mostrar o modal.
        const jaTem = !!JSON.parse(sessionStorage.getItem("brave_cliente_portaria") || "null");
        if (!jaTem && arr.length > 0) {
          const lastId = (clienteId && localStorage.getItem(lastKey)) || "";
          const restore = arr.find((p) => p.id === lastId) || (arr.length === 1 ? arr[0] : null);
          if (restore) {
            sessionStorage.setItem("brave_cliente_portaria", JSON.stringify(restore));
            window.dispatchEvent(new CustomEvent("brave:portaria-changed", { detail: restore }));
            setTemSelecionada(true);
          }
        }
      })
      .catch(() => { if (!cancel) setPortarias([]); });
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const naRotaDePortarias = location.pathname.startsWith("/cliente/portarias");
  // Bloqueio só decide depois do fetch (portarias !== null) para evitar flash.
  const fetchDone = portarias !== null;
  const bloquear = fetchDone && !temSelecionada && !naRotaDePortarias;

  // Sincroniza estado global para o ClienteLayout desabilitar UI
  useEffect(() => {
    window.__bravePortariaBloqueada = bloquear;
    window.dispatchEvent(new CustomEvent("brave:portaria-bloqueio-changed", { detail: { bloquear } }));
  }, [bloquear]);

  const handleSelect = (p) => {
    sessionStorage.setItem("brave_cliente_portaria", JSON.stringify(p));
    if (clienteId) localStorage.setItem(lastKey, p.id);
    window.dispatchEvent(new CustomEvent("brave:portaria-changed", { detail: p }));
    setTemSelecionada(true);
    toast.success(`Portaria definida: ${p.codigo} · ${p.nome}`);
  };

  if (!bloquear) return null;
  if (portarias === null) return null; // ainda carregando

  const semPortarias = portarias.length === 0;
  const titulo = semPortarias ? "Nenhuma portaria cadastrada" : "Selecione uma portaria";

  return (
    <div
      data-testid="portaria-guard-modal"
      className="fixed inset-0 z-[9000] flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="w-[92%] max-w-md rounded-xl border border-amber-400/40 bg-brand-dark/95 shadow-2xl p-6 space-y-5">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-11 h-11 rounded-full bg-amber-500/15 border border-amber-400/40">
            {semPortarias ? (
              <AlertTriangle className="w-5 h-5 text-amber-300" />
            ) : (
              <Building2 className="w-5 h-5 text-amber-300" />
            )}
          </div>
          <h3 className="text-lg font-heading font-bold text-brand-ice">{titulo}</h3>
        </div>

        {semPortarias ? (
          <p className="text-sm text-brand-ice/85 leading-relaxed">
            Não existem portarias ou recepções cadastradas. É obrigatório pelo menos uma portaria no sistema.
          </p>
        ) : (
          <>
            <p className="text-sm text-brand-ice/85 leading-relaxed">
              Selecione a portaria de trabalho ou o local onde esta estação de trabalho está instalada.
            </p>
            <div className="max-h-60 overflow-auto rounded-lg border border-brand-ice/15 divide-y divide-brand-ice/10">
              {portarias.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  data-testid={`portaria-guard-option-${p.codigo}`}
                  onClick={() => handleSelect(p)}
                  className="w-full flex items-center gap-3 px-3 py-2 text-left text-sm text-brand-ice/90 hover:bg-white/10 hover:text-white transition-colors"
                >
                  <Building2 className="w-4 h-4 text-brand-ice/40 shrink-0" />
                  <span className="flex-1 truncate">
                    <span className="font-mono text-xs text-brand-ice/55 mr-2">{p.codigo}</span>
                    <span>{p.nome}</span>
                    {p.apelido ? (
                      <span className="text-xs text-brand-ice/50 ml-2">· {p.apelido}</span>
                    ) : null}
                  </span>
                  <Check className="w-4 h-4 text-emerald-400/0 group-hover:text-emerald-400" />
                </button>
              ))}
            </div>
          </>
        )}

        <div className="flex justify-end gap-2 pt-1 border-t border-brand-ice/10">
          <button
            type="button"
            data-testid="portaria-guard-incluir"
            onClick={() => navigate("/cliente/portarias")}
            className="inline-flex items-center gap-2 px-4 h-10 rounded-md bg-brand-medium hover:bg-brand-dark text-white text-sm font-heading font-bold transition-colors"
          >
            <Building2 className="w-4 h-4" />
            Incluir Portarias
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
